import React, { useState, useEffect } from 'react';
import { db } from '../firebase/config';
import { callClaude } from '../utils/claudeProxy';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, CircularProgress, IconButton,
  TextField, Alert, LinearProgress, Divider, Chip, Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LagbasLogo from '../components/LagbasLogo';
import PrintIcon from '@mui/icons-material/Print';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

/* ─── PDF helpers (reused in preview + print) ─────────────────────────── */
function InfoTable({ title, rows }) {
  const filtered = rows.filter(r => r.value);
  if (!filtered.length) return null;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <div style={{ width: 3, height: 12, backgroundColor: '#FF6B00', borderRadius: 2 }} />
        <span style={{ fontWeight: 700, fontSize: '0.62rem', color: '#FF6B00',
          letterSpacing: 1.5, textTransform: 'uppercase' }}>{title}</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {filtered.map((row, i) => (
            <tr key={i} style={{ backgroundColor: i % 2 === 0 ? 'rgba(255,107,0,0.04)' : 'transparent' }}>
              <td style={{ fontSize: '0.68rem', color: '#999', padding: '3px 6px',
                width: 110, verticalAlign: 'top', whiteSpace: 'nowrap' }}>{row.label}</td>
              <td style={{ fontSize: '0.68rem', fontWeight: 600, color: '#1a1a1a',
                padding: '3px 6px', verticalAlign: 'top', wordBreak: 'break-word' }}>{row.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionHeading({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
      <div style={{ width: 3, height: 13, backgroundColor: '#FF6B00', borderRadius: 2, flexShrink: 0 }} />
      <span style={{ fontWeight: 700, fontSize: '0.7rem', color: '#FF6B00',
        letterSpacing: 1.5, textTransform: 'uppercase' }}>{children}</span>
    </div>
  );
}

function RefPreviewContent({ project, company, fields }) {
  const { name, role, consultantName, budget, startDate, endDate,
    projectDescription, ourAssignment, taskList, keywords } = fields;
  return (
    <div style={{ backgroundColor: 'white', fontFamily: '"Segoe UI", Arial, sans-serif' }}>
      {/* SIDHUVUD */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        padding: '20px 32px 16px', borderBottom: '3px solid #1a1a1a' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: 2,
            color: '#FF6B00', textTransform: 'uppercase', marginBottom: 3 }}>Referensuppdrag</div>
          <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#1a1a1a',
            lineHeight: 1.1, marginBottom: 3 }}>{name}</div>
          {role && <div style={{ fontSize: '0.85rem', color: '#555', fontWeight: 400 }}>{role}</div>}
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 24 }}>
          {company?.logo
            ? <img src={company.logo} alt="logo" style={{ height: 36, objectFit: 'contain', display: 'block', marginLeft: 'auto', marginBottom: 3 }} />
            : <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#1a1a1a' }}>Lagbas<span style={{ color: '#FF6B00' }}>N</span></div>}
          {project.projectNumber && <div style={{ fontSize: '0.6rem', color: '#aaa', marginTop: 2 }}>Nr: {project.projectNumber}</div>}
        </div>
      </div>

      {/* BILD + REFERENSPERSON */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e0e0e0' }}>
        {project.image && (
          <div style={{ width: '50%', flexShrink: 0, borderRight: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5', height: 200, overflow: 'hidden' }}>
            <img src={project.image} alt={project.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </div>
        )}
        <div style={{ width: project.image ? '50%' : '100%', flexShrink: 0, padding: '14px 18px' }}>
          <InfoTable title="Referensperson hos bestallare" rows={[
            { label: 'Uppdragsgivare', value: project.client },
            { label: 'Org.nr', value: project.clientOrgNumber },
            { label: 'Namn', value: project.contactName },
            { label: 'Befattning', value: project.contactRole },
            { label: 'E-post', value: project.contactEmail },
            { label: 'Telefon', value: project.contactPhone },
          ]} />
        </div>
      </div>

      {/* UPPGIFTER KRING UPPDRAG */}
      <div style={{ padding: '14px 32px', backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
        <InfoTable title="Uppgifter kring uppdrag" rows={[
          { label: 'Projekt', value: name },
          { label: 'Budget', value: budget },
          { label: 'Start', value: startDate },
          { label: 'Slut', value: endDate },
          { label: 'Roll', value: role },
          { label: 'Konsult', value: consultantName },
          { label: 'Typ', value: project.projectType },
        ]} />
      </div>

      {/* BESKRIVNING */}
      <div style={{ padding: '20px 32px 24px' }}>
        {projectDescription && (
          <div style={{ marginBottom: 16 }}>
            <SectionHeading>Beskrivning av uppdrag</SectionHeading>
            <p style={{ fontSize: '0.83rem', lineHeight: 1.8, color: '#333', margin: 0, paddingLeft: 10 }}>
              {projectDescription}
            </p>
          </div>
        )}
        {ourAssignment && (
          <div style={{ marginBottom: 16 }}>
            <SectionHeading>Vart uppdrag</SectionHeading>
            <p style={{ fontSize: '0.83rem', lineHeight: 1.8, color: '#333', margin: 0, paddingLeft: 10 }}>
              {ourAssignment}
            </p>
          </div>
        )}
        {taskList.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <SectionHeading>Uppgifter</SectionHeading>
            <div style={{ paddingLeft: 10 }}>
              {taskList.map((task, i) => (
                <div key={i} style={{ display: 'flex', gap: 7, marginBottom: 4 }}>
                  <span style={{ color: '#FF6B00', fontSize: '0.83rem', flexShrink: 0, lineHeight: 1.7 }}>•</span>
                  <span style={{ fontSize: '0.83rem', lineHeight: 1.7, color: '#333' }}>{task}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {keywords.length > 0 && (
          <div style={{ paddingTop: 14, marginTop: 6, borderTop: '1px solid #f0f0f0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 5 }}>
              <div style={{ width: 3, height: 10, backgroundColor: '#ccc', borderRadius: 2 }} />
              <span style={{ fontWeight: 700, fontSize: '0.6rem', color: '#999',
                letterSpacing: 1.5, textTransform: 'uppercase' }}>Nyckelord</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {keywords.map((k, i) => (
                <span key={i} style={{ fontSize: '0.62rem', padding: '2px 7px',
                  border: '1px solid #ddd', borderRadius: 3, color: '#666', display: 'inline-block' }}>{k}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* SIDFOT */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '8px 32px', backgroundColor: '#1a1a1a' }}>
        <span style={{ fontSize: '0.6rem', color: '#777' }}>
          {[company?.name, company?.address].filter(Boolean).join(' | ')}
        </span>
        {company?.logo && (
          <img src={company.logo} alt="logo"
            style={{ height: 16, objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.4 }} />
        )}
        <span style={{ fontSize: '0.6rem', color: '#777' }}>
          {[company?.phone, company?.website, company?.email].filter(Boolean).join(' | ')}
        </span>
      </div>
    </div>
  );
}

/* ─── AI ───────────────────────────────────────────────────────────────── */
async function getSuggestionsFromAI(project, tender, currentFields) {
  const response = await callClaude({
      model: 'claude-opus-5',
      max_tokens: 4000,
      messages: [{
        role: 'user',
        content: `Du ska ge förslag på hur ett referensuppdrag kan anpassas mot ett specifikt anbud.
OBS: Du ska INTE ersätta befintliga texter. Du ger förslag som användaren kan välja att använda eller ignorera.

REFERENSUPPDRAG (aktuella texter):
Namn: ${currentFields.name}
Roll: ${currentFields.role}
Beskrivning: ${currentFields.projectDescription}
Vårt uppdrag: ${currentFields.ourAssignment}
Uppgifter: ${currentFields.taskList.join(', ')}
Nyckelord: ${currentFields.keywords.join(', ')}

ANBUD ATT ANPASSA MOT:
Titel: ${tender.name || ''}
Klient: ${tender.client || ''}
Efterfrågade roller: ${(tender.requiredRoles || []).join(', ')}
Nyckelord: ${(tender.keywords || []).join(', ')}
Krav (ska): ${(tender.mustRequirements || []).join(', ')}
Krav (bör): ${(tender.shouldRequirements || []).join(', ')}

Returnera ENDAST ett JSON-objekt:
{
  "notes": "Kort motivering – vad i anbudet matchar detta referensuppdrag och hur det bör lyftas fram",
  "projectDescription": "Föreslagen beskrivning",
  "ourAssignment": "Föreslaget om vårt uppdrag",
  "taskList": ["föreslagen uppgift 1", "..."],
  "keywords": ["föreslaget nyckelord 1", "..."]
}`
      }]
    });
  const data = response;
  const raw = data.content[0].text;
  const clean = raw.replace(/```json|```/g, '').trim();
  return JSON.parse(clean);
}

/* ─── Field label ───────────────────────────────────────────────────────── */
function FieldLabel({ children }) {
  return (
    <Typography variant="caption" fontWeight="bold" color="text.secondary"
      sx={{ textTransform: 'uppercase', letterSpacing: 1, display: 'block', mt: 1.5, mb: 0.5 }}>
      {children}
    </Typography>
  );
}

/* ─── Suggestion box ───────────────────────────────────────────────────── */
function SuggestionBox({ text, onUse }) {
  if (!text) return null;
  const displayText = Array.isArray(text) ? text.join('\n') : text;
  return (
    <Paper variant="outlined" sx={{
      mt: 0.5, p: 1.5, bgcolor: '#fffbf0',
      borderColor: '#FF6B00', borderStyle: 'dashed'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <LightbulbIcon sx={{ fontSize: 15, color: '#FF6B00', mt: 0.2, flexShrink: 0 }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" color="text.secondary"
            sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
            AI-förslag
          </Typography>
          <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap', color: '#555' }}>
            {displayText}
          </Typography>
        </Box>
        <Button size="small" variant="outlined"
          onClick={onUse}
          sx={{ fontSize: '0.6rem', py: 0.2, px: 0.8, flexShrink: 0,
            borderColor: '#FF6B00', color: '#FF6B00',
            '&:hover': { bgcolor: '#fff3ec' } }}>
          Använd
        </Button>
      </Box>
    </Paper>
  );
}

/* ─── Main component ────────────────────────────────────────────────────── */
export default function ReferenceVariant() {
  const { tenderId, refId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tender, setTender] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adapting, setAdapting] = useState(false);
  const [adaptProgress, setAdaptProgress] = useState(0);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');
  const [suggestions, setSuggestions] = useState(null);

  // Editable metadata
  const [variantName, setVariantName] = useState('');
  const [variantRole, setVariantRole] = useState('');
  const [variantConsultant, setVariantConsultant] = useState('');
  const [variantBudget, setVariantBudget] = useState('');
  const [variantStartDate, setVariantStartDate] = useState('');
  const [variantEndDate, setVariantEndDate] = useState('');

  // Editable text
  const [projectDescription, setProjectDescription] = useState('');
  const [ourAssignment, setOurAssignment] = useState('');
  const [taskListText, setTaskListText] = useState('');

  // Keywords as chips: array of {label, selected}
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState('');

  const variantDocId = `${tenderId}_${refId}`;

  useEffect(() => {
    async function load() {
      const [projSnap, tenderSnap] = await Promise.all([
        getDoc(doc(db, 'referenceProjects', refId)),
        getDoc(doc(db, 'tenders', tenderId)),
      ]);
      if (!projSnap.exists()) { setLoading(false); return; }

      const proj = { id: projSnap.id, ...projSnap.data() };
      const tenderData = tenderSnap.exists() ? { id: tenderSnap.id, ...tenderSnap.data() } : {};
      setProject(proj);
      setTender(tenderData);

      if (proj.companyId) {
        const compSnap = await getDoc(doc(db, 'companies', proj.companyId));
        if (compSnap.exists()) setCompany(compSnap.data());
      }

      const variantSnap = await getDoc(doc(db, 'referenceVariants', variantDocId));
      if (variantSnap.exists()) {
        const v = variantSnap.data();
        setVariantName(v.name || proj.name || '');
        setVariantRole(v.role || proj.role || '');
        setVariantConsultant(v.consultantName || proj.consultantName || '');
        setVariantBudget(v.budget || proj.budget || '');
        setVariantStartDate(v.startDate || proj.startDate || '');
        setVariantEndDate(v.endDate || proj.endDate || '');
        setProjectDescription(v.projectDescription || proj.projectDescription || '');
        setOurAssignment(v.ourAssignment || proj.ourAssignment || '');
        setTaskListText((v.taskList || proj.taskList || []).join('\n'));
        // Build keyword chips: union of original + saved
        const savedKws = new Set(v.keywords || []);
        const origKws = proj.keywords || [];
        const allKws = [...new Set([...origKws, ...savedKws])];
        setKeywords(allKws.map(k => ({ label: k, selected: savedKws.has(k) })));
      } else {
        setVariantName(proj.name || '');
        setVariantRole(proj.role || '');
        setVariantConsultant(proj.consultantName || '');
        setVariantBudget(proj.budget || '');
        setVariantStartDate(proj.startDate || '');
        setVariantEndDate(proj.endDate || '');
        setProjectDescription(proj.projectDescription || '');
        setOurAssignment(proj.ourAssignment || '');
        setTaskListText((proj.taskList || []).join('\n'));
        setKeywords((proj.keywords || []).map(k => ({ label: k, selected: true })));
      }
      setLoading(false);
    }
    load();
  }, [tenderId, refId, variantDocId]);

  function toggleKeyword(idx) {
    setKeywords(prev => prev.map((k, i) => i === idx ? { ...k, selected: !k.selected } : k));
  }

  function addKeyword() {
    const kw = newKeyword.trim();
    if (!kw) return;
    setKeywords(prev => {
      if (prev.find(k => k.label.toLowerCase() === kw.toLowerCase())) return prev;
      return [...prev, { label: kw, selected: true }];
    });
    setNewKeyword('');
  }

  function removeKeyword(idx) {
    setKeywords(prev => prev.filter((_, i) => i !== idx));
  }

  const selectedKeywords = keywords.filter(k => k.selected).map(k => k.label);
  const taskList = taskListText.split('\n').map(s => s.trim()).filter(Boolean);

  const currentFields = {
    name: variantName, role: variantRole, consultantName: variantConsultant,
    budget: variantBudget, startDate: variantStartDate, endDate: variantEndDate,
    projectDescription, ourAssignment,
    taskList, keywords: selectedKeywords,
  };

  async function handleGetSuggestions() {
    if (!tender || !project) return;
    setAdapting(true);
    setError('');
    setAdaptProgress(30);
    try {
      const result = await getSuggestionsFromAI(project, tender, currentFields);
      setAdaptProgress(90);
      setSuggestions(result);
      setAdaptProgress(100);
    } catch (err) {
      console.error(err);
      setError('AI-förslag misslyckades. Försök igen.');
    } finally {
      setAdapting(false);
      setAdaptProgress(0);
    }
  }

  async function handleSave() {
    await setDoc(doc(db, 'referenceVariants', variantDocId), {
      tenderId, refId,
      name: variantName, role: variantRole,
      consultantName: variantConsultant, budget: variantBudget,
      startDate: variantStartDate, endDate: variantEndDate,
      projectDescription, ourAssignment, taskList,
      keywords: selectedKeywords,
      updatedAt: new Date()
    });
    setSaved('Variant sparad!');
    setTimeout(() => setSaved(''), 2000);
  }

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
      <CircularProgress />
    </Box>
  );

  if (!project) return (
    <Box sx={{ p: 4 }}>
      <Typography>Referensuppdraget hittades inte.</Typography>
      <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>Tillbaka</Button>
    </Box>
  );

  return (
    <Box sx={{ height: '100vh', bgcolor: '#e8e8e8', display: 'flex', flexDirection: 'column' }}>

      {/* Toolbar */}
      <Box className="no-print" sx={{
        bgcolor: '#1a1a1a', color: 'white', py: 1.5, px: 3, flexShrink: 0,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LagbasLogo />
          <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.2)', mx: -0.5 }} />
          <IconButton color="inherit" size="small" onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              {variantName || project.name} – Variant för anbud
            </Typography>
            {tender?.name && (
              <Typography variant="caption" sx={{ color: '#aaa' }}>{tender.name}</Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button variant="outlined"
            startIcon={adapting ? <CircularProgress size={14} color="inherit" /> : <AutoFixHighIcon />}
            onClick={handleGetSuggestions}
            disabled={adapting}
            sx={{ color: '#FF6B00', borderColor: '#FF6B00',
              '&:hover': { borderColor: '#e55a00', bgcolor: 'rgba(255,107,0,0.08)' } }}>
            {adapting ? 'Hämtar förslag...' : 'AI-förslag'}
          </Button>
          <Button variant="outlined" startIcon={<SaveIcon />} onClick={handleSave}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.4)',
              '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
            Spara
          </Button>
          <Button variant="contained" startIcon={<PrintIcon />} onClick={() => window.print()}
            sx={{ bgcolor: 'white', color: '#1a1a1a', '&:hover': { bgcolor: '#e0e0e0' } }}>
            Skriv ut / PDF
          </Button>
        </Box>
      </Box>

      {adapting && (
        <LinearProgress variant="determinate" value={adaptProgress} className="no-print"
          sx={{ flexShrink: 0, '& .MuiLinearProgress-bar': { bgcolor: '#FF6B00' } }} />
      )}

      {/* Two-column 50/50 */}
      <Box className="no-print" sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* LEFT – edit panel */}
        <Box sx={{
          width: '50%', flexShrink: 0, overflowY: 'auto',
          bgcolor: 'background.paper',
          borderRight: '1px solid', borderColor: 'divider',
          p: 2.5,
        }}>
          {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1.5 }}>{error}</Alert>}
          {saved && <Alert severity="success" sx={{ mb: 1.5 }}>{saved}</Alert>}

          {suggestions?.notes && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: '#f3f4ff', borderColor: '#7986cb' }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <LightbulbIcon sx={{ fontSize: 15, color: '#3949ab', mt: 0.2, flexShrink: 0 }} />
                <Box>
                  <Typography variant="caption" fontWeight="bold" color="#3949ab" sx={{ display: 'block' }}>
                    AI-analys
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {suggestions.notes}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          )}

          {/* ── Metadata ── */}
          <Typography variant="overline" color="text.secondary"
            sx={{ letterSpacing: 1.5, fontWeight: 700 }}>
            Uppgifter
          </Typography>
          <Divider sx={{ mb: 1 }} />

          <FieldLabel>Projektnamn</FieldLabel>
          <TextField fullWidth size="small" value={variantName}
            onChange={e => setVariantName(e.target.value)} />

          <FieldLabel>Roll</FieldLabel>
          <TextField fullWidth size="small" value={variantRole}
            onChange={e => setVariantRole(e.target.value)} />

          <FieldLabel>Konsult</FieldLabel>
          <TextField fullWidth size="small" value={variantConsultant}
            onChange={e => setVariantConsultant(e.target.value)} />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <FieldLabel>Budget</FieldLabel>
              <TextField fullWidth size="small" value={variantBudget}
                onChange={e => setVariantBudget(e.target.value)} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FieldLabel>Start</FieldLabel>
              <TextField fullWidth size="small" value={variantStartDate}
                onChange={e => setVariantStartDate(e.target.value)} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FieldLabel>Slut</FieldLabel>
              <TextField fullWidth size="small" value={variantEndDate}
                onChange={e => setVariantEndDate(e.target.value)} />
            </Box>
          </Box>

          {/* ── Texter ── */}
          <Typography variant="overline" color="text.secondary"
            sx={{ letterSpacing: 1.5, fontWeight: 700, display: 'block', mt: 2 }}>
            Texter
          </Typography>
          <Divider sx={{ mb: 1 }} />

          <FieldLabel>Beskrivning av uppdrag</FieldLabel>
          <TextField fullWidth multiline minRows={3} size="small"
            value={projectDescription}
            onChange={e => setProjectDescription(e.target.value)} />
          <SuggestionBox
            text={suggestions?.projectDescription}
            onUse={() => setProjectDescription(suggestions.projectDescription)} />

          <FieldLabel>Vårt uppdrag</FieldLabel>
          <TextField fullWidth multiline minRows={2} size="small"
            value={ourAssignment}
            onChange={e => setOurAssignment(e.target.value)} />
          <SuggestionBox
            text={suggestions?.ourAssignment}
            onUse={() => setOurAssignment(suggestions.ourAssignment)} />

          <FieldLabel>Uppgifter (en per rad)</FieldLabel>
          <TextField fullWidth multiline minRows={4} size="small"
            value={taskListText}
            onChange={e => setTaskListText(e.target.value)}
            helperText="En uppgift per rad" />
          <SuggestionBox
            text={suggestions?.taskList}
            onUse={() => setTaskListText(suggestions.taskList.join('\n'))} />

          {/* ── Nyckelord ── */}
          <Typography variant="overline" color="text.secondary"
            sx={{ letterSpacing: 1.5, fontWeight: 700, display: 'block', mt: 2 }}>
            Nyckelord
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Klicka för att inkludera/exkludera. Kryssa-bort = visas inte i förhandsgranskningen.
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mb: 1.5 }}>
            {keywords.map((kw, i) => (
              <Chip
                key={i}
                label={kw.label}
                size="small"
                onClick={() => toggleKeyword(i)}
                onDelete={() => removeKeyword(i)}
                variant={kw.selected ? 'filled' : 'outlined'}
                sx={{
                  bgcolor: kw.selected ? '#1a1a1a' : 'transparent',
                  color: kw.selected ? 'white' : 'text.secondary',
                  borderColor: kw.selected ? '#1a1a1a' : 'divider',
                  '& .MuiChip-deleteIcon': {
                    color: kw.selected ? 'rgba(255,255,255,0.6)' : 'text.disabled',
                    '&:hover': { color: kw.selected ? 'white' : 'text.secondary' }
                  }
                }}
              />
            ))}
          </Box>

          {suggestions?.keywords?.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1, mb: 1.5, bgcolor: '#fffbf0', borderColor: '#FF6B00', borderStyle: 'dashed' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <LightbulbIcon sx={{ fontSize: 14, color: '#FF6B00' }} />
                <Typography variant="caption" fontWeight="bold" color="#FF6B00">
                  AI-föreslagna nyckelord
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {suggestions.keywords.map((kw, i) => {
                  const exists = keywords.find(k => k.label.toLowerCase() === kw.toLowerCase());
                  return (
                    <Chip key={i} label={kw} size="small"
                      onClick={() => {
                        if (exists) {
                          const idx = keywords.findIndex(k => k.label.toLowerCase() === kw.toLowerCase());
                          setKeywords(prev => prev.map((k, ii) => ii === idx ? { ...k, selected: true } : k));
                        } else {
                          setKeywords(prev => [...prev, { label: kw, selected: true }]);
                        }
                      }}
                      sx={{ fontSize: '0.65rem', bgcolor: '#fff3e0', color: '#FF6B00',
                        border: '1px solid #FF6B00', cursor: 'pointer',
                        '&:hover': { bgcolor: '#ffe0b2' } }}
                    />
                  );
                })}
              </Box>
            </Paper>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" placeholder="Lägg till nyckelord..."
              value={newKeyword}
              onChange={e => setNewKeyword(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addKeyword(); }}
              sx={{ flex: 1, '& .MuiInputBase-input': { py: 0.7, fontSize: '0.85rem' } }} />
            <IconButton size="small" onClick={addKeyword} disabled={!newKeyword.trim()}
              sx={{ border: '1px solid', borderColor: 'divider' }}>
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* RIGHT – live preview */}
        <Box sx={{ width: '50%', overflowY: 'auto', p: 3, bgcolor: '#e0e0e0' }}>
          <Box sx={{
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            borderRadius: 1, overflow: 'hidden'
          }}>
            <RefPreviewContent
              project={project}
              company={company}
              fields={currentFields}
            />
          </Box>
        </Box>
      </Box>

      {/* Print-only A4 */}
      <Box className="print-only" sx={{ display: 'none', '@media print': { display: 'block' } }}>
        <RefPreviewContent project={project} company={company} fields={currentFields} />
      </Box>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 0; }
          html, body { margin: 0; padding: 0;
            -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
        }
      `}</style>
    </Box>
  );
}
